import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import {
  CreateProposalGroupInput,
  CreateProposalGroupMutation,
} from '@/gql/graphql';

// `__typename` is selected by hand: nothing injects it into these documents,
// and the generated type declares it optional — so a union narrowed on it
// compiles and then reads `undefined` at runtime.
const CREATE_PROPOSAL_GROUP_MUTATION = graphql(`
  mutation CreateProposalGroup($input: CreateProposalGroupInput!) {
    createProposalGroup(input: $input) {
      id
      sections {
        __typename
        ... on ProposalItemGroupSection {
          key
          groups {
            key
          }
        }
      }
    }
  }
`);

/**
 * The key of the group the mutation just appended to `sectionKey`. Undefined if
 * the section is not in the response, which a caller should treat as "could not
 * navigate" rather than an error.
 */
export const appendedGroupKey = (
  data: CreateProposalGroupMutation,
  sectionKey: string
): string | undefined => {
  for (const section of data.createProposalGroup.sections) {
    if (
      section.__typename === 'ProposalItemGroupSection' &&
      section.key === sectionKey
    ) {
      // The server appends a new group when `position` is omitted.
      return section.groups.at(-1)?.key;
    }
  }
  return undefined;
};

const useCreateProposalGroup = (args?: {
  proposalId?: string;
  options?: UseMutationOptions<
    CreateProposalGroupMutation,
    unknown,
    CreateProposalGroupInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: CreateProposalGroupInput) => {
      return gqlClient.request(CREATE_PROPOSAL_GROUP_MUTATION, { input });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalId) {
        void queryClient.invalidateQueries({
          queryKey: proposalKeys.proposal(args.proposalId),
        });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useCreateProposalGroup;
